/**
 * Consultium Group — FAQ knowledge base.
 *
 * Content is sourced verbatim from the official Consultium Business Services
 * portal FAQ document so that the visible accordion content on-page matches
 * the FAQPage JSON-LD schema exactly (a Google requirement for FAQ rich
 * results).
 *
 * - `serviceFaqs[<service-id>]` returns the FAQ list for that specific service
 *   detail page (used in ServiceDetail.jsx).
 * - `faqSections` powers the dedicated /faqs page and Home page snippet.
 * - `buildFaqSchema(faqs)` returns a FAQPage JSON-LD object ready for
 *   `<StructuredData>`.
 */

// Full text — Q&A pairs grouped by topic
export const faqSections = [
  {
    id: 'about',
    title: 'About Consultium Business Services',
    items: [
      {
        q: 'What is Consultium Business Services?',
        a: 'Consultium Business Services supports South African businesses with company registration, CIPC compliance, SARS and tax services, B-BBEE-related services and broader business solutions. The service is designed to help entrepreneurs establish, maintain and grow compliant businesses.',
      },
      {
        q: 'Which business services are available?',
        a: 'Services include private-company registration, tax clearance assistance, beneficial ownership declarations, annual returns, public officer appointments, director or shareholder changes, B-BBEE support, ISO certification support, marketing solutions and related consulting services.',
      },
    ],
  },
  {
    id: 'company-registration',
    title: 'Company Registration',
    items: [
      {
        q: 'What does registering a company mean?',
        a: 'Registering a company means incorporating it with the Companies and Intellectual Property Commission, or CIPC. A registered private company is a separate legal person that can enter contracts, employ people, hold assets, open accounts and meet tax obligations in its own name.',
      },
      {
        q: 'What information is normally required to register a company?',
        a: 'The application normally requires proposed company names, registered-address information, the details of directors and shareholders, and clear identity documents. Additional information may be requested depending on the directors, ownership structure and regulatory checks.',
      },
      {
        q: 'How long does company registration take?',
        a: 'Consultium advertises delivery of a new private-company registration in approximately 24 to 48 hours, subject to CIPC processing, name approval and receipt of complete and acceptable supporting documents.',
      },
      {
        q: 'Which documents are normally issued after registration?',
        a: 'Registration normally produces a CIPC registration certificate, company registration number and Memorandum of Incorporation. Depending on the selected service, further records such as share certificates, tax-registration confirmation or compliance documents may also be prepared.',
      },
    ],
  },
  {
    id: 'company-names',
    title: 'Company Names',
    items: [
      {
        q: 'How can I improve the likelihood that a company name is accepted?',
        a: 'Submit distinctive name choices and avoid names that are identical or confusingly similar to existing companies or protected brands. Combining meaningful words, checking online availability and providing several alternatives can reduce delays.',
      },
    ],
  },
  {
    id: 'identity-documents',
    title: 'Identity Documents',
    items: [
      {
        q: 'What are the typical identity-document requirements?',
        a: 'South African directors generally provide a clear copy of a valid identity document. Foreign directors generally provide a valid passport. When certification is required, the copy should be complete, legible, recently certified and show the commissioner of oaths details, signature and date.',
      },
    ],
  },
  {
    id: 'cipc-compliance',
    title: 'CIPC Compliance',
    items: [
      {
        q: 'What is beneficial ownership?',
        a: 'Beneficial ownership identifies the natural persons who ultimately own or control a company, directly or indirectly. Companies must keep accurate ownership information and submit the required beneficial ownership declarations to CIPC.',
      },
      {
        q: 'Why must annual returns be filed?',
        a: 'CIPC annual returns confirm that a company remains active and update prescribed company information. Missing annual returns can lead to non-compliance and may ultimately place the company at risk of deregistration.',
      },
      {
        q: 'Can directors or shareholders be changed?',
        a: 'Yes. Changes must be properly authorised, supported by the required records and submitted through the relevant CIPC process. Shareholder changes may also require updates to share certificates, the securities register and beneficial ownership information.',
      },
    ],
  },
  {
    id: 'tax-compliance',
    title: 'Tax Compliance',
    items: [
      {
        q: 'What is a company tax reference number?',
        a: 'It is a unique number issued by SARS to identify the company for income-tax purposes. It is different from the CIPC registration number and from a director’s personal tax number.',
      },
      {
        q: 'What is a Public Officer?',
        a: 'A Public Officer is the individual appointed to act as the company’s representative for tax matters with SARS. The person helps ensure that registrations, returns, notices and other tax obligations are handled appropriately.',
      },
      {
        q: 'What is an ITR14?',
        a: 'An ITR14 is the company’s annual income-tax return. It reports income, allowable expenditure, profit or loss, taxable income and the resulting tax payable or refundable. It must generally be filed even if the company did not trade.',
      },
      {
        q: 'When is the ITR14 due?',
        a: 'A company’s ITR14 is generally due within 12 months after its financial year-end. The exact due date should be confirmed on the company’s SARS profile and compliance records.',
      },
      {
        q: 'What information is commonly needed for an ITR14?',
        a: 'Common requirements include accounting records, bank statements, annual financial statements, a trial balance, tax computations and supporting schedules. The information must reconcile and should be retained in case SARS requests verification.',
      },
      {
        q: 'What is an IRP6 provisional tax return?',
        a: 'IRP6 is used to estimate and pay company income tax during the financial year. Provisional tax is a payment mechanism, not a separate type of tax, and helps spread the expected income-tax liability across the year.',
      },
      {
        q: 'When are provisional tax returns submitted?',
        a: 'Companies generally submit a first provisional return halfway through the financial year and a second at year-end. A top-up payment may be relevant later if the earlier estimates and payments were insufficient.',
      },
      {
        q: 'Must a dormant company still submit tax returns?',
        a: 'Yes. A registered company may still have filing obligations even when it earned no income or did not trade. Nil returns may be required until the relevant tax type is formally deregistered or the company is otherwise legally closed.',
      },
      {
        q: 'What is SARS eFiling used for?',
        a: 'SARS eFiling is the online platform used to manage tax registrations, returns, payments, notices, supporting documents and tax-compliance requests for individuals and businesses.',
      },
      {
        q: 'What is an EMP201?',
        a: 'An EMP201 is the monthly employer declaration used for PAYE, UIF and, where applicable, SDL. Registered employers generally submit the declaration and related payment monthly, including a nil declaration where required.',
      },
      {
        q: 'What are the penalties for late tax submissions?',
        a: 'Consequences depend on the tax type and circumstances. They may include fixed or percentage-based penalties, recurring monthly administrative penalties, interest on unpaid tax, estimated assessments and a non-compliant SARS status. For outstanding company income-tax returns, administrative penalties may recur monthly after the applicable SARS final-demand process. Late provisional, payroll or VAT payments may also attract penalties and interest. Submit outstanding returns promptly and seek professional assistance where necessary.',
      },
      {
        q: 'How can a business reduce the risk of tax penalties?',
        a: 'Maintain accurate accounting records, monitor filing calendars, keep representative details current, submit nil returns where required, reconcile SARS accounts regularly and address notices immediately. If payment cannot be made in full, engage SARS promptly about available payment arrangements.',
      },
      {
        q: 'Can a SARS penalty be challenged or remitted?',
        a: 'A taxpayer may request remission or dispute a penalty where the law permits and valid grounds exist. The outstanding return should still be submitted promptly, and the request should include clear reasons and supporting evidence. Remission is not automatic.',
      },
      {
        q: 'What is tax clearance or Tax Compliance Status?',
        a: 'Tax Compliance Status reflects whether SARS records show that required returns and payments are up to date, subject to permitted arrangements. Businesses may use a Tax Compliance Status PIN when demonstrating compliance to clients, funders or tender authorities.',
      },
    ],
  },
  {
    id: 'support',
    title: 'Support',
    items: [
      {
        q: 'How can I contact Consultium Business Services?',
        a: 'Contact the Consultium team through the website, call 012 824 3708, WhatsApp 079 042 4911 or email hello@consultiumgroup.co.za. Service availability, pricing and turnaround times should be confirmed when requesting assistance.',
      },
    ],
  },
];

// Build a fast lookup by section id
const sectionMap = Object.fromEntries(faqSections.map((s) => [s.id, s.items]));

// Helpers to pull FAQs for a service page. Each service maps to one or more
// section ids from the knowledge base above.
const SERVICE_TO_SECTIONS = {
  'company-registration': ['company-registration', 'company-names', 'identity-documents'],
  'beneficial-ownership': [
    { section: 'cipc-compliance', pick: ['What is beneficial ownership?'] },
  ],
  'annual-returns': [
    { section: 'cipc-compliance', pick: ['Why must annual returns be filed?'] },
  ],
  'director-changes': [
    { section: 'cipc-compliance', pick: ['Can directors or shareholders be changed?'] },
  ],
  'public-officer': [
    { section: 'tax-compliance', pick: ['What is a Public Officer?'] },
  ],
  'tax-clearance': [
    { section: 'tax-compliance', pick: ['What is tax clearance or Tax Compliance Status?'] },
  ],
  'paye-uif': [
    { section: 'tax-compliance', pick: ['What is an EMP201?'] },
  ],
  'personal-liability': ['company-registration'],
  'npc-registration': ['company-registration'],
  'cooperative-registration': ['company-registration'],
};

export const getFaqsForService = (serviceId) => {
  const spec = SERVICE_TO_SECTIONS[serviceId];
  if (!spec) return [];
  const items = [];
  spec.forEach((entry) => {
    if (typeof entry === 'string') {
      items.push(...(sectionMap[entry] || []));
    } else {
      const secItems = sectionMap[entry.section] || [];
      entry.pick.forEach((q) => {
        const found = secItems.find((it) => it.q === q);
        if (found) items.push(found);
      });
    }
  });
  return items;
};

export const buildFaqSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((it) => ({
    '@type': 'Question',
    name: it.q,
    acceptedAnswer: { '@type': 'Answer', text: it.a },
  })),
});
